import Box from '@mui/material/Box';
import { ReactNode, useMemo } from 'react';
import { IntlProvider } from 'react-intl';

import { useLanguageContext } from './LanguageContext';
import { useTranslations } from './useTranslations';

type FormatXMLElementFn<T, R = string | T | (string | T)[]> = (parts: Array<string | T>) => R;

const defaultLocale = 'en-GB';

function IntlWithRichText({
  children,
  locale,
  messages,
}: {
  children: React.ReactNode;
  locale: string;
  messages: Record<string, string>;
}) {
  const richElements = useMemo((): Record<string, FormatXMLElementFn<ReactNode>> => {
    return {
      // Avoid real heading tags: rich-text snippets often sit inside dialog titles (already an h2).
      h1: (chunks) => (
        <Box component="span" sx={{ display: 'block', fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.334 }}>
          {chunks}
        </Box>
      ),
      h2: (chunks) => <h2>{chunks}</h2>,
      h3: (chunks) => <h3>{chunks}</h3>,
      h4: (chunks) => <h4>{chunks}</h4>,
      h5: (chunks) => <h5>{chunks}</h5>,
      h6: (chunks) => <h6>{chunks}</h6>,
      p: (chunks) => <p>{chunks}</p>,
      b: (chunks) => <b>{chunks}</b>,
      span: (chunks) => <span>{chunks}</span>,
    };
  }, []);

  return (
    <IntlProvider
      defaultRichTextElements={richElements}
      messages={messages}
      locale={locale}
      onError={(error) => {
        // ignore missing translations, we see it in spreadsheet
        if (error.code !== 'MISSING_TRANSLATION') {
          console.warn(error);
        }
      }}
    >
      {children}
    </IntlProvider>
  );
}

const TranslationsProvider = ({ children }: { children: React.ReactNode }) => {
  const { language } = useLanguageContext();
  const translationMessages = useTranslations(import.meta.env.VITE_TRANSLATIONS_MODE, language, defaultLocale);

  return (
    <IntlWithRichText locale={language || defaultLocale} messages={translationMessages}>
      {children}
    </IntlWithRichText>
  );
};

export default TranslationsProvider;
